import { useCallback, useEffect, useMemo } from 'react';
import { type SearchRefs, useSearchInputContext } from 'src/common/components/Search/SearchInputProvider';
import useDateFilterRange from 'src/common/hooks/useDateFilterRange';

export type UseSearchDateHandlersReturn = {
  handleDateFilterChange: (selectedDates: Date[]) => void;
  setRangeOption: (option: number) => void;
  rangeOption: number;
  dateRangeOptions: {
    value: number;
    labelI18nKey: string;
    onRangeClick: (rangeDates: Date[]) => void;
  }[];
  highlightDates: {
    'react-datepicker__day--highlighted--search-when-input': Date[];
  }[];
};

/** Used in both popovers and sheets. */
export const useSearchDateHandlers = (refs?: Partial<SearchRefs>): UseSearchDateHandlersReturn => {
  const { filterDates, setFilterDates } = useSearchInputContext();

  const dateRanges = [-1, 0, 1, 2, 3, 7];
  const highlightDates = [{
    'react-datepicker__day--highlighted--search-when-input': filterDates,
  }];

  const { rangeOption, setRangeOption } = useDateFilterRange(filterDates, dateRanges);

  const handleDateFilterChange = useCallback(
    (selectedDates: Date[]) => {
      refs?.when?.current?.focus();
      setFilterDates(selectedDates);
      if (selectedDates?.length > 0) {
        setRangeOption((prev) => Math.max(0, prev));
      } else {
        setRangeOption(-1);
      }
    },
    [refs?.when, setFilterDates, setRangeOption],
  );

  // Respond to resetting dates, since the rangeOption is local state, not context
  useEffect(() => {
    if (!filterDates.length) {
      setRangeOption(-1);
    }
  }, [filterDates, setRangeOption]);

  const dateRangeOptions = useMemo(() => {
    const list = [
      {
        value: 0,
        labelI18nKey: 'filter-range-buttons--exact-date',
        onRangeClick: (rangeDates: Date[]) => {
          setFilterDates(rangeDates);
          setRangeOption(0);
        },
      },
    ];

    [1, 2, 3, 7].forEach((option) => {
      list.push({
        value: option,
        labelI18nKey: `filter-range-buttons--option-${option === 1 ? 'single' : 'plural'}`,
        onRangeClick: (rangeDates: Date[] | []) => {
          setFilterDates(rangeDates);
          setRangeOption(option);
        },
      });
    });
    return list;
  }, [setFilterDates, setRangeOption]);

  return {
    handleDateFilterChange,
    setRangeOption,
    rangeOption,
    dateRangeOptions,
    highlightDates,
  };
};
